"use client";

import { Position, TextAlign } from "@/types";
import { getCalculatedHeight, getCommonTextStyles, getRgbaBackground, lightTextColors } from "@/utils/dabateUtil";
import { safeT } from "@/utils/safeT";
import { TextField } from "@mui/material";
import { styled, SxProps, Theme } from "@mui/material/styles";
import { useTranslations } from "next-intl";
import { KeyboardEvent } from "react";

const CustomTextField = styled(TextField)(({ theme }) => ({
  "& .MuiInputBase-root": {
    borderRadius: "4px",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderWidth: "1px", // 수정: 테두리 두께 증가
    borderColor: "rgba(255, 255, 255, 0.1)",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderWidth: "1px",
    borderColor: "rgba(255, 255, 255, 0.3)",
  },
  "& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    // 추가: 포커스 시 테두리 스타일 명시
    borderWidth: "1px",
    borderColor: "rgba(255, 255, 255, 0.7)", // 밝은 색상으로 가시성 확보
  },
  "& .MuiInputLabel-root": {
    color: "rgba(255, 255, 255, 0.7)",
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#ffffff",
  },
}));

export const positionStyles: Record<Position, SxProps<Theme>> = {
  "top-left": { top: "10px", left: "10px" },
  "top-center": { top: "10px", left: "50%", transform: "translateX(-50%)" },
  "top-right": { top: "10px", right: "10px" },
  "middle-left": { top: "50%", left: "10px", transform: "translateY(-50%)" },
  "middle-center": {
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
  },
  "middle-right": { top: "50%", right: "10px", transform: "translateY(-50%)" },
  "bottom-left": { bottom: "10px", left: "10px" },
  "bottom-center": {
    bottom: "10px",
    left: "50%",
    transform: "translateX(-50%)",
  },
  "bottom-right": { bottom: "10px", right: "10px" },
};

export const getPositionStyle = (position: Position): SxProps<Theme> => {
  if (typeof position === "string" && position in positionStyles) {
    return positionStyles[position as keyof typeof positionStyles];
  }
  return positionStyles["middle-center"];
};

export interface TitleTextBoxProps {
  position?: Position;
  color?: string;
  backgroundColor?: string;
  opacity?: number;
  value: string;
  onChange: (value: string) => void;
  textAlign?: TextAlign;
  width?: string | number;
  fontSize?: 22 | 26 | 30; // 수정: fontSize 타입 명시
  maxLength?: number;
  fontWeight?: React.CSSProperties["fontWeight"];
  placeholder?: string;
}

export default function TitleTextBox({
  position = "middle-center",
  color = "#ffffff",
  backgroundColor = "#000000",
  opacity = 0.15,
  value,
  onChange,
  textAlign = "left",
  width = "300px",
  fontSize = 26,
  maxLength = 100,
  fontWeight = "normal",
  placeholder,
}: TitleTextBoxProps) {
  const rgbaBackground = getRgbaBackground(opacity, backgroundColor);
  const calculatedHeight = getCalculatedHeight(fontSize);
  const t = useTranslations();
  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      const lineCount = value.split("\n").length;
      if (lineCount >= 3) {
        e.preventDefault();
      }
    }
  };

  return (
    <CustomTextField
      placeholder={
        placeholder
          ? placeholder
          : safeT(t, "ui.placeholder.debateTitlePrompt", {
              defaultMessage: "토론 제목을 멋지게 적어주세요!",
            })
      }
      value={value}
      onChange={(e) => onChange(e.target.value)}
      variant="outlined"
      multiline
      minRows={1}
      maxRows={3}
      slotProps={{
        htmlInput: { maxLength },
        input: {
          sx: {
            // 추가: 플레이스홀더 스타일
            "&::placeholder": {
              color: "rgba(255, 255, 255, 0.7)",
              opacity: 1,
              fontSize,
              lineHeight: `${fontSize * 1.4}px`, // 플레이스홀더 수직 중앙 정렬
              textAlign,
              fontWeight,
            },
          },
        },
      }}
      onKeyDown={handleKeyDown}
      sx={{
        position: "absolute",
        ...positionStyles[position],
        width,
        "& .MuiInputBase-root": {
          display: "flex",
          alignItems: "center",
          minHeight: calculatedHeight, // 수정: minHeight를 root로 이동
        },
        "& .MuiInputBase-input": {
          textAlign,
          fontWeight,
          color,
          fontSize,
          textShadow: lightTextColors.includes(color)
            ? "1px 1px 4px rgba(0, 0, 0, 1)"
            : "1px 1px 4px rgba(255, 255, 255, 1)",
        },
        "& .MuiInputBase-root.MuiInputBase-multiline": {
          backgroundColor: rgbaBackground,
          whiteSpace: "normal",
          overflowWrap: "break-word",
          boxSizing: "border-box",
          paddingX: 0.5,
          paddingY: 1,
          lineHeight: `${fontSize * 1.4}px`, // 수정: fontSize에 비례한 lineHeight
        },
      }}
    />
  );
}
